<template>
  <div class="max-w-4xl mx-auto px-4 py-8">
    <!-- 標題區塊 -->
    <div class="mb-8 text-center">
      <h1 class="text-4xl font-bold text-wood-800 mb-4 flex items-center justify-center">
        <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft mr-4"></div>
        編輯拳譜
      </h1>
      <p class="text-wood-600 text-lg">修改拳譜的基本資訊</p>
    </div>

    <!-- 載入狀態 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="text-center">
        <div class="w-16 h-16 border-4 border-wood-300 border-t-wood-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-wood-600 text-lg">載入拳譜資料中...</p>
      </div>
    </div>

    <!-- 錯誤狀態 -->
    <div v-else-if="error" class="text-center py-20">
      <div class="bg-red-100 border border-red-400 rounded-2xl p-8 max-w-md mx-auto">
        <i class="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
        <p class="text-red-700 mb-4">{{ error }}</p>
        <button @click="loadBoxingChart"
                class="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-300">
          重新載入
        </button>
      </div>
    </div>

    <!-- 表單區塊 -->
    <div v-else class="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-wood-200 post-shadow">
      <div class="bg-gradient-to-r from-wood-600 to-wood-700 text-white p-6">
        <div class="flex items-center space-x-3">
          <i class="fas fa-edit text-2xl"></i>
          <h2 class="text-2xl font-bold">拳譜資訊</h2>
        </div>
      </div>

      <div class="p-8">
        <!-- 錯誤訊息 -->
        <div v-if="validationErrors.length > 0" class="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <div class="flex items-start space-x-3">
            <i class="fas fa-exclamation-circle text-red-500 text-xl mt-1"></i>
            <div class="flex-1">
              <h4 class="font-bold text-red-800 mb-2">發生 {{ validationErrors.length }} 個錯誤:</h4>
              <ul class="list-disc list-inside space-y-1">
                <li v-for="(error, index) in validationErrors" :key="index" class="text-red-700 text-sm">
                  {{ error }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- 拳譜名稱 -->
          <div>
            <label for="boxing-chart-name" class="block text-wood-800 font-semibold mb-2">
              <i class="fas fa-signature mr-2 text-wood-600"></i>
              拳譜名稱
            </label>
            <input
              id="boxing-chart-name"
              v-model="formData.name"
              type="text"
              placeholder="例如: 楊家老架太極拳37式"
              required
              class="w-full px-4 py-3 border-2 border-wood-200 rounded-xl focus:border-wood-500 focus:ring-2 focus:ring-wood-200 transition-all duration-200 outline-none"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-200': formData.name === '' && submitted }"
            />
            <p class="mt-2 text-sm text-wood-600 flex items-start">
              <i class="fas fa-info-circle mr-2 mt-0.5 flex-shrink-0"></i>
              <span>此名稱必須與 public/ 目錄下的 JSON 檔案名稱相同(不含 .json 副檔名)</span>
            </p>
          </div>

          <!-- 拳譜描述 -->
          <div>
            <label for="boxing-chart-description" class="block text-wood-800 font-semibold mb-2">
              <i class="fas fa-align-left mr-2 text-wood-600"></i>
              拳譜描述
            </label>
            <textarea
              id="boxing-chart-description"
              v-model="formData.description"
              rows="6"
              placeholder="請輸入拳譜的簡短描述..."
              required
              class="w-full px-4 py-3 border-2 border-wood-200 rounded-xl focus:border-wood-500 focus:ring-2 focus:ring-wood-200 transition-all duration-200 outline-none resize-none"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-200': formData.description === '' && submitted }"
            ></textarea>
            <p class="mt-2 text-sm text-wood-600 flex items-start">
              <i class="fas fa-info-circle mr-2 mt-0.5 flex-shrink-0"></i>
              <span>簡要說明此拳譜的特色、來源或適用對象</span>
            </p>
          </div>

          <!-- JSON 檔案狀態 -->
          <div v-if="boxingChart.id" class="rounded-xl p-4"
               :class="boxingChart.json_file_exists ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'">
            <div class="flex items-start space-x-3">
              <i class="text-xl mt-1"
                 :class="boxingChart.json_file_exists ? 'fas fa-check-circle text-green-500' : 'fas fa-exclamation-triangle text-yellow-500'"></i>
              <div class="flex-1">
                <p :class="boxingChart.json_file_exists ? 'text-green-800' : 'text-yellow-800'">
                  <span v-if="boxingChart.json_file_exists">
                    對應的 JSON 檔案已存在: <code class="bg-white/50 px-2 py-1 rounded">{{ boxingChart.json_file_path }}</code>
                  </span>
                  <span v-else>
                    警告: 找不到對應的 JSON 檔案 <code class="bg-white/50 px-2 py-1 rounded">{{ boxingChart.json_file_path }}</code>
                    <br>
                    <small class="text-yellow-700">請確認檔案是否存在於 public/ 目錄</small>
                  </span>
                </p>
              </div>
            </div>
          </div>

          <!-- 按鈕組 -->
          <div class="flex items-center space-x-4 pt-4">
            <button
              type="submit"
              :disabled="submitting"
              class="text-wood-700 border-1 border-wood-300 flex-1 px-8 py-3 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <i class="fas mr-2" :class="submitting ? 'fa-spinner fa-spin' : 'fa-save'"></i>
              {{ submitting ? '儲存中...' : '更新拳譜' }}
            </button>
            <button
              type="button"
              @click="goBack"
              :disabled="submitting"
              class="px-8 py-3 bg-white/80 hover:bg-wood-100 text-wood-700 font-semibold rounded-full border-1 border-wood-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fas fa-times mr-2"></i>
              取消
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 返回列表按鈕 -->
    <div class="mt-8 text-center">
      <button @click="goToList"
              class="px-8 py-3 bg-gradient-to-r from-wood-200 to-wood-300 hover:from-wood-300 hover:to-wood-400 text-wood-800 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
        <i class="fas fa-arrow-left mr-2"></i>
        返回拳譜列表
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'

// Props
const props = defineProps({
  boxingChartId: {
    type: [Number, String],
    required: true
  }
})

// 響應式資料
const loading = ref(true)
const error = ref(null)
const submitting = ref(false)
const submitted = ref(false)
const validationErrors = ref([])

const boxingChart = ref({
  id: null,
  name: '',
  description: '',
  json_file_exists: false,
  json_file_path: ''
})

const formData = ref({
  name: '',
  description: ''
})

// CSRF Token
const getCsrfToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]')
  return token ? token.getAttribute('content') : ''
}

// 載入拳譜資料
const loadBoxingChart = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await axios.get(`/boxing_charts/${props.boxingChartId}.json`)
    boxingChart.value = response.data
    formData.value = {
      name: response.data.name,
      description: response.data.description
    }
    console.log('拳譜資料載入成功:', response.data)
  } catch (err) {
    console.error('載入拳譜資料失敗:', err)
    error.value = `載入拳譜資料失敗: ${err.response?.data?.error || err.message}`
  } finally {
    loading.value = false
  }
}

// 表單驗證
const validateForm = () => {
  validationErrors.value = []

  if (!formData.value.name || formData.value.name.trim() === '') {
    validationErrors.value.push('拳譜名稱不能為空')
  }

  if (!formData.value.description || formData.value.description.trim() === '') {
    validationErrors.value.push('拳譜描述不能為空')
  }

  return validationErrors.value.length === 0
}

// 提交表單
const handleSubmit = async () => {
  submitted.value = true

  if (!validateForm()) {
    // 滾動到錯誤訊息
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  submitting.value = true
  validationErrors.value = []

  try {
    const response = await axios.patch(
      `/boxing_charts/${props.boxingChartId}`,
      {
        boxing_chart: {
          name: formData.value.name.trim(),
          description: formData.value.description.trim()
        }
      },
      {
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    )

    console.log('拳譜更新成功:', response.data)

    // 顯示成功訊息並重導向
    alert('拳譜更新成功！')
    window.location.href = `/boxing_charts/${props.boxingChartId}`
  } catch (err) {
    console.error('更新拳譜失敗:', err)

    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      validationErrors.value = [err.response?.data?.error || err.message || '更新失敗，請稍後再試']
    }

    // 滾動到錯誤訊息
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } finally {
    submitting.value = false
  }
}

// 返回上一頁
const goBack = () => {
  if (confirm('確定要取消編輯嗎？未儲存的變更將會遺失。')) {
    window.history.back()
  }
}

// 返回列表
const goToList = () => {
  window.location.href = '/boxing_charts'
}

// 組件掛載
onMounted(() => {
  console.log('拳譜編輯組件已載入, ID:', props.boxingChartId)
  loadBoxingChart()
})
</script>

<style scoped>
/* 陰陽太極圖 */
.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}

/* 柔和脈動動畫 */
@keyframes pulse-soft {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-pulse-soft {
  animation: pulse-soft 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* 貼文陰影效果 */
.post-shadow {
  box-shadow: 0 4px 6px -1px rgba(101, 67, 33, 0.1),
              0 2px 4px -1px rgba(101, 67, 33, 0.06);
}

.post-shadow:hover {
  box-shadow: 0 10px 15px -3px rgba(101, 67, 33, 0.1),
              0 4px 6px -2px rgba(101, 67, 33, 0.05);
}
</style>
